import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Autocomplete,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { fetchCustomers, fetchTaskRecords, fetchEmployees, fetchImageBlob } from "../../Utils/api";
import { useAuth } from "../../../src/Auth/AuthContext";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import "./reports.css";

dayjs.extend(quarterOfYear);
dayjs.extend(utc);
dayjs.extend(timezone);

const timezoneForState = (state) => {
  switch ((state || "").toUpperCase().trim()) {
    case "FL": case "NY": case "NJ": case "PA": case "OH": case "NC":
    case "SC": case "GA": case "VA": case "WV": case "ME": case "NH":
    case "VT": case "MA": case "RI": case "CT": case "MD": case "DE":
    case "MI": case "IN": case "KY": case "TN":
      return "America/New_York";
    case "TX": case "IL": case "WI": case "MN": case "IA": case "MO":
    case "AR": case "LA": case "MS": case "AL": case "OK": case "KS":
    case "NE": case "SD": case "ND":
      return "America/Chicago";
    case "AZ":
      return "America/Phoenix";
    case "CO": case "NM": case "UT": case "WY": case "MT": case "ID":
      return "America/Denver";
    case "CA": case "WA": case "OR": case "NV":
      return "America/Los_Angeles";
    default:
      return "America/Chicago";
  }
};

// Formats a UTC ISO timestamp as "h:mm AM/PM TZ" in the customer's local timezone.
const formatTimeWithTz = (isoDate, state) => {
  if (!isoDate) return "N/A";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneForState(state),
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(isoDate));
  } catch { return "N/A"; }
};

// Formats a UTC ISO timestamp as "MM/DD/YYYY, h:mm AM/PM TZ" in the customer's local timezone.
const formatDateTimeWithTz = (isoDate, state) => {
  if (!isoDate) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneForState(state),
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    }).format(new Date(isoDate));
  } catch { return ""; }
};

const fmtLevel = (val) => {
  if (val == null) return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return Number.isInteger(n) ? n.toFixed(1) : n;
};

const OPTIONAL_COLS = [
  // Readings
  { key: "poolCondition",    label: "Pool Condition",    group: "Readings",  render: (log) => log.outCond      ?? "—" },
  { key: "ph",               label: "pH Level",          group: "Readings",  render: (log) => fmtLevel(log.phLvl) },
  { key: "chlorine",         label: "Chlorine Level",    group: "Readings",  render: (log) => fmtLevel(log.chlrLvl) },
  { key: "waterLevel",       label: "Water Level",       group: "Readings",  render: (log) => log.outWtrLevel  ?? "—" },
  { key: "algae",            label: "Algae",             group: "Readings",  render: (log) => log.outAlg       ?? "—" },
  { key: "alkalinity",       label: "Alkalinity",        group: "Readings",  render: (log) => log.alklLvl ?? "—" },
  { key: "stabilizer",       label: "Stabilizer",        group: "Readings",  render: (log) => log.stabLvl ?? "—" },
  // Chemicals
  { key: "chlorineTabs",     label: "Chlorine Tablets",  group: "Chemicals", render: (log) => log.chemChlrTab || "—" },
  { key: "chlorineGranules", label: "Chlorine Granules", group: "Chemicals", render: (log) => log.chemChlrGrn || "—" },
  { key: "chlorineLiquid",   label: "Chlorine Liquid",   group: "Chemicals", render: (log) => log.chemChlrLiq || "—" },
  { key: "muriaticAcid",     label: "Muriatic Acid",     group: "Chemicals", render: (log) => log.chemMrtcAcd  || "—" },
  { key: "sodaAsh",          label: "Soda Ash",          group: "Chemicals", render: (log) => log.chemSodaAsh  || "—" },
  { key: "stabilizerAdded",  label: "Stabilizer Added",  group: "Chemicals", render: (log) => log.chemStabAdd  || "—" },
  { key: "sodiumBicarb",     label: "Sodium Bicarbonate",group: "Chemicals", render: (log) => log.chemSodBic   || "—" },
  // Services
  { key: "pumpBasket",       label: "Pump Basket",       group: "Services",  render: (log) => log.srvcClnPmpBsk  || "—" },
  { key: "backwash",         label: "Backwash Main",     group: "Services",  render: (log) => log.srvcBckWshMain || "—" },
  { key: "skimBasket",       label: "Skim Basket",       group: "Services",  render: (log) => log.srvcSkimBsk    || "—" },
  { key: "vacuum",           label: "Vacuum",            group: "Services",  render: (log) => log.srvcVcum       || "—" },
  { key: "wallBrush",        label: "Wall Brush",        group: "Services",  render: (log) => log.srvcWallBrsh   || "—" },
  { key: "floorBrush",       label: "Floor Brush",       group: "Services",  render: (log) => log.srvcFlrBrsh    || "—" },
  { key: "filterBreakdown",  label: "Filter Breakdown",  group: "Services",  render: (log) => log.serviceFilterBreakDown || "—" },
  { key: "filterPressure",   label: "Filter Pressure (PSI)", group: "Services", render: (log) => log.filterPressurePsi != null ? log.filterPressurePsi : "—" },
  // Notes
  { key: "logComments",      label: "Log Comments",      group: "Notes",     render: (log) => log.logCmnt  || "—" },
  { key: "serviceComments",  label: "Service Comments",  group: "Notes",     render: (log) => log.srvcCmnt || "—" },
];

const COL_GROUPS = ["Readings", "Chemicals", "Services", "Notes"];
const DEFAULT_COLS = new Set(["poolCondition", "ph", "chlorine"]);

const HEADER_CELL_SX = {
  backgroundColor: "#002244",
  color: "white",
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
};

const Reports = () => {

    const { companyId } = useAuth();
    const location = useLocation();

  const [customerList, setCustomerList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [dateRangeType, setDateRangeType] = useState("30days");
  const [endDate, setEndDate] = useState(dayjs().endOf("day")); // today
  const [startDate, setStartDate] = useState(
    dayjs().subtract(30, "day").startOf("day"),
  );
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCols, setSelectedCols] = useState(new Set(DEFAULT_COLS));
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerLoading, setViewerLoading] = useState(false);

  const getFullImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== "string") return null;
    const trimmed = imagePath.trim();
    if (!trimmed) return null;
    try {
      const url = new URL(trimmed);
      // Reject bucket-root-only URLs produced by an empty S3 key
      if (!url.pathname || url.pathname === "/") return null;
      return trimmed;
    } catch {
      // Not a full URL — treat as an S3 object key; the backend proxy accepts both
      return trimmed;
    }
  };

  const handleViewImages = async (evidences) => {
    const keys = evidences.map(e => getFullImageUrl(e)).filter(Boolean);
    if (keys.length === 0) {
      setErrorMessage("No images available for this task.");
      return;
    }
    setViewerImages([]);
    setViewerLoading(true);
    setImageViewerOpen(true);
    const loaded = [];
    for (const key of keys) {
      try {
        const blob = await fetchImageBlob(key);
        loaded.push(URL.createObjectURL(blob));
      } catch (e) {
        console.error("Failed to load evidence image:", e);
      }
    }
    setViewerImages(loaded);
    setViewerLoading(false);
  };

  const handleCloseImageViewer = () => {
    viewerImages.forEach(url => URL.revokeObjectURL(url));
    setViewerImages([]);
    setImageViewerOpen(false);
  };

  // Load customers on mount
  useEffect(() => {
    const loadCustomers = async () => {
      if (!companyId) {
        console.log("No companyId available, skipping customer fetch");
        return;
      }

      try {
        const data = await fetchCustomers(companyId, "Y");

        // Validate response
        if (!data || !Array.isArray(data)) {
          console.warn("Invalid customer data received");
          setCustomerList([]);
          return;
        }

        const formattedData = data
          .filter(customer => customer && customer.customerId) // Filter out invalid entries
          .map((customer) => ({
            id: customer.customerId,
            name: `${customer.custFirstName || ''} ${customer.custLastName || ''}`.trim(),
            address: `${customer.custAddress || ''}, ${customer.custCity || ''}, ${customer.custZip || ''}`,
          }));

        setCustomerList(formattedData);
      } catch (error) {
        console.error("Error loading customers:", error);
        setCustomerList([]);
      }
    };
    loadCustomers();
  }, [companyId]);

  useEffect(() => {
    const loadEmployees = async () => {
      if (!companyId) {
        console.log("No companyId available, skipping employee fetch");
        return;
      }

      try {
        const data = await fetchEmployees(companyId);

        // Validate response
        if (!data || !Array.isArray(data)) {
          console.warn("Invalid employee data received");
          setEmployeeList([]);
          return;
        }

        const formatted = data
          .filter(emp => emp && emp.employeeId) // Filter out invalid entries
          .map((emp) => ({
            id: emp.employeeId,
            name: `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),
            role: emp.empDesignation || '',
            phone: emp.contactNumber || '',
          }));

        setEmployeeList(formatted);
      } catch (error) {
        console.error("Error loading employees:", error);
        setEmployeeList([]);
      }
    };

    loadEmployees();
  }, [companyId]);

  // Handle customer from navigation state (from Manage Customers "Logs" button)
  useEffect(() => {
    if (location.state?.customer && customerList.length > 0) {
      const customerFromNav = location.state.customer;
      // Find the matching customer in the list
      const matchingCustomer = customerList.find(c => c.id === customerFromNav.id);

      if (matchingCustomer) {
        setSelectedCustomer(matchingCustomer);
      }
    }
  }, [location.state, customerList]);

  // Auto-generate report when customer is selected from navigation
  useEffect(() => {
    if (location.state?.customer && selectedCustomer && customerList.length > 0) {
      // Only trigger if this customer was set from navigation
      const customerFromNav = location.state.customer;
      if (selectedCustomer.id === customerFromNav.id) {
        handleGenerateReport();
      }
    }
  }, [selectedCustomer]);

  // Handle date range preset changes
  const handleDateRangeChange = (event, newValue) => {
  if (!newValue) return;

  setDateRangeType(newValue);

  if (newValue !== "custom") {
    updateDatesForPreset(newValue);
  }
};


  // Update end date based on period type and start date
    const updateDatesForPreset = (periodType) => {
  const today = dayjs().endOf("day");
  let calculatedStartDate;

  switch (periodType) {
    case "30days":
      calculatedStartDate = today.subtract(30, "day");
      break;
    case "90days":
      calculatedStartDate = today.subtract(90, "day");
      break;
    case "annual":
      calculatedStartDate = today.subtract(365, "day");
      break;
    default:
      return;
  }

  setEndDate(today);
  setStartDate(calculatedStartDate.startOf("day"));
};


  // Handle start date change
  const handleStartDateChange = (newValue) => {
  if (!newValue) return;
  setStartDate(dayjs(newValue).startOf("day"));
};

  // Generate report
const handleGenerateReport = async () => {
  setLoading(true);
  setShowResults(false);
  setErrorMessage("");

  try {
    // Validate company ID
    if (!companyId) {
      throw new Error("Company ID is required");
    }

    // Validate dates
    if (!startDate?.isValid?.() || !endDate?.isValid?.()) {
      throw new Error("Invalid date range");
    }

    // Validate that end date is not before start date
    if (endDate.isBefore(startDate, 'day')) {
      setErrorMessage("End date cannot be before start date");
      setLoading(false);
      return;
    }

    const fromDate = startDate.isBefore(endDate) ? startDate : endDate;
    const toDate = startDate.isAfter(endDate) ? startDate : endDate;

    const data = await fetchTaskRecords({
      companyId: companyId,
      startDate: fromDate.format("YYYY-MM-DD"),
      endDate: toDate.format("YYYY-MM-DD"),
      customerId: selectedCustomer?.id || undefined, // Convert null to undefined
      employeeId: selectedEmployee?.id || undefined, // Convert null to undefined
    });

    // Validate response
    if (!data) {
      console.warn("No data returned from API");
      setReportData([]);
    } else {
      setReportData(data);
    }

    setShowResults(true);
  } catch (error) {
    console.error("Error generating report:", error);
    const errorMsg = error.response?.data?.message || error.message || "Failed to generate report. Please try again.";
    setErrorMessage(errorMsg);
    setReportData([]);
  } finally {
    setLoading(false);
  }
};



  // Export to Excel with embedded images using ExcelJS
  const handleExportToExcel = async () => {
    if (reportData.length === 0) return;

    setExcelLoading(true);

    try {
      // Create a new workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Logs Report");

      // Define columns
      worksheet.columns = [
        { header: "Task ID", key: "taskId", width: 10 },
        { header: "Log ID", key: "logId", width: 10 },
        { header: "Customer Name", key: "customerName", width: 20 },
        { header: "Address", key: "address", width: 30 },
        { header: "Employee Name", key: "employeeName", width: 20 },
        { header: "Task Date", key: "taskDate", width: 12 },
        { header: "Check-In", key: "checkIn", width: 16 },
        { header: "Check-Out", key: "checkOut", width: 16 },
        { header: "Status", key: "status", width: 12 },
        { header: "Pool Condition", key: "poolCondition", width: 15 },
        { header: "Water Level", key: "waterLevel", width: 12 },
        { header: "Algae", key: "algae", width: 10 },
        { header: "pH Level", key: "phLevel", width: 10 },
        { header: "Chlorine Level", key: "chlorineLevel", width: 14 },
        { header: "Alkalinity Level", key: "alkalinityLevel", width: 16 },
        { header: "Stabilizer Level", key: "stabilizerLevel", width: 16 },
        { header: "Chlorine Tablets", key: "chlorineTablets", width: 16 },
        { header: "Chlorine Granules", key: "chlorineGranules", width: 16 },
        { header: "Chlorine Liquid", key: "chlorineLiquid", width: 16 },
        { header: "Muriatic Acid", key: "muriaticAcid", width: 14 },
        { header: "Soda Ash", key: "sodaAsh", width: 14 },
        { header: "Stabilizer Added", key: "stabilizerAdded", width: 16 },
        { header: "Sodium Bicarbonate", key: "sodiumBicarb", width: 18 },
        { header: "Clean Pump Basket", key: "cleanPumpBasket", width: 18 },
        { header: "Backwash Main", key: "backwashMain", width: 14 },
        { header: "Skim Basket", key: "skimBasket", width: 12 },
        { header: "Vacuum", key: "vacuum", width: 10 },
        { header: "Wall Brush", key: "wallBrush", width: 12 },
        { header: "Floor Brush", key: "floorBrush", width: 12 },
        { header: "Filter Pressure (PSI)", key: "filterPressurePsi", width: 18 },
        { header: "Filter Breakdown", key: "filterBreakdown", width: 16 },
        { header: "Log Comments", key: "logComments", width: 30 },
        { header: "Service Comments", key: "serviceComments", width: 30 },
        { header: "Pool Images", key: "poolImages", width: 60 },
      ];

      // Style the header row
      worksheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      worksheet.getRow(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF002244" },
      };
      worksheet.getRow(1).alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      // Add data rows with images
      for (let idx = 0; idx < reportData.length; idx++) {
        const item = reportData[idx];
        const log = item.taskDetails || {};
        const customer = item.customer || {};

        // Add text data row
        const row = worksheet.addRow({
          taskId: item.taskId || log.taskId || "",
          logId: log.logId || "",
          customerName: log.customerName || "",
          address: [customer.custAddress, customer.custCity, customer.custState, customer.custZip].filter(Boolean).join(", "),
          employeeName: (() => {
            const assignedName = employeeList.find(e => e.id === log.empId)?.name || "";
            return log.taskCompletedBy && assignedName && assignedName !== log.employeeName
              ? `${log.employeeName || ""} (Sub for: ${assignedName})`
              : log.employeeName || "";
          })(),
          taskDate: log.checkOut || log.checkIn || log.updtTs || log.creaTs || log.workOrderTaskDueDate || item.taskDate || "",
          checkIn: log.checkIn ? formatDateTimeWithTz(log.checkIn, customer.custState) : "",
          checkOut: log.checkOut ? formatDateTimeWithTz(log.checkOut, customer.custState) : "",
          status: log.taskStatus || "",
          poolCondition: log.outCond || "",
          waterLevel: log.outWtrLevel || "",
          algae: log.outAlg || "",
          phLevel: log.phLvl != null ? fmtLevel(log.phLvl) : "",
          chlorineLevel: log.chlrLvl != null ? fmtLevel(log.chlrLvl) : "",
          alkalinityLevel: log.alklLvl || "",
          stabilizerLevel: log.stabLvl || "",
          chlorineTablets: log.chemChlrTab || "",
          chlorineGranules: log.chemChlrGrn || "",
          chlorineLiquid: log.chemChlrLiq || "",
          muriaticAcid: log.chemMrtcAcd || "",
          sodaAsh: log.chemSodaAsh || "",
          stabilizerAdded: log.chemStabAdd || "",
          sodiumBicarb: log.chemSodBic || "",
          cleanPumpBasket: log.srvcClnPmpBsk || "",
          backwashMain: log.srvcBckWshMain || "",
          skimBasket: log.srvcSkimBsk || "",
          vacuum: log.srvcVcum || "",
          wallBrush: log.srvcWallBrsh || "",
          floorBrush: log.srvcFlrBrsh || "",
          filterPressurePsi: log.filterPressurePsi != null ? log.filterPressurePsi : "",
          filterBreakdown: log.serviceFilterBreakDown || "",
          logComments: log.logCmnt || "",
          serviceComments: log.srvcCmnt || "",
          poolImages: "", // Will be filled with images
        });

        // Collect images
        const images = [];
        if (log.imgEvd) images.push(log.imgEvd);
        if (log.evidences && Array.isArray(log.evidences))
          images.push(...log.evidences);

        // Try to add images to the Excel file
        if (images.length > 0) {
          let imageAddedSuccessfully = false;

          for (let i = 0; i < images.length; i++) {
            const fullUrl = getFullImageUrl(images[i]);
            if (fullUrl) {
              try {
                // Fetch image as array buffer via the backend proxy (the S3 bucket has no
                // CORS rule allowing this origin to read the response body directly).
                const blob = await fetchImageBlob(fullUrl);
                const arrayBuffer = await blob.arrayBuffer();

                // Add image to workbook
                const imageId = workbook.addImage({
                  buffer: arrayBuffer,
                  extension: "jpeg",
                });

                // Calculate row height for image (increase row height to accommodate image)
                const imageHeight = 100; // pixels
                row.height = Math.max(row.height || 15, imageHeight * 0.75); // Convert pixels to points

                // Add image to cell
                worksheet.addImage(imageId, {
                  tl: { col: 33, row: row.number - 1 }, // Last column (Pool Images)
                  ext: { width: 150, height: imageHeight },
                  editAs: "oneCell",
                });

                imageAddedSuccessfully = true;

                // Only add first image to avoid overcrowding
                break;
              } catch (error) {
                console.error(
                  `Failed to load image ${i + 1}/${images.length}:`,
                  fullUrl,
                  error,
                );
              }
            }
          }

          // If no images were added successfully, show error message in cell
          if (!imageAddedSuccessfully) {
            row.getCell("poolImages").value =
              "[Images failed to load - CORS error or network issue]";
            row.getCell("poolImages").font = { color: { argb: "FFC80000" } }; // Red text

            // Add URLs as note
            const urls = images
              .map((img) => getFullImageUrl(img))
              .filter((url) => url)
              .join("\n");
            if (urls) {
              row.getCell("poolImages").note = `Image URLs:\n${urls}`;
            }
          }
        } else {
          row.getCell("poolImages").value = "No images";
        }
      }

      // Auto-fit rows and apply borders
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (rowNumber > 1) {
            cell.alignment = { vertical: "top", wrapText: true };
          }
        });
      });

      // Generate buffer and save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `Report_${
        selectedCustomer
          ? selectedCustomer.name.replace(/\s/g, "_")
          : "All_Customers"
      }_${startDate.format("YYYY-MM-DD")}_to_${endDate.format("YYYY-MM-DD")}.xlsx`;

      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error generating Excel file:", error);
      setErrorMessage("Failed to generate Excel file. Please try again.");
    } finally {
      setExcelLoading(false);
    }
  };

  // Helper function to load image and convert to base64. Goes through the backend image
  // proxy (instead of fetching the S3 URL directly) because the bucket has no CORS rule
  // allowing this origin to read the response body via fetch/XHR.
  const loadImageAsBase64 = async (url) => {
    try {
      const blob = await fetchImageBlob(url);
      const mimeType = blob.type;

      // Convert blob to base64
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          // Load image to get dimensions
          const img = new Image();
          img.onload = () => {
            // Determine image format from MIME type
            let format = "JPEG"; // default
            if (mimeType.includes("png")) {
              format = "PNG";
            } else if (mimeType.includes("jpeg") || mimeType.includes("jpg")) {
              format = "JPEG";
            } else if (mimeType.includes("gif")) {
              format = "GIF";
            } else if (mimeType.includes("webp")) {
              format = "WEBP";
            }

            resolve({
              dataURL: reader.result,
              width: img.width,
              height: img.height,
              format: format,
            });
          };
          img.onerror = () => reject(new Error("Failed to decode image"));
          img.src = reader.result;
        };
        reader.onerror = () => reject(new Error("Failed to read blob"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error loading image:", error);
      throw error;
    }
  };

  // Export to PDF using jsPDF
  const handleExportToPDF = async () => {
    if (reportData.length === 0) return;

    setPdfLoading(true);

    // Create new PDF document
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Title
    doc.setFontSize(18);
    doc.setTextColor(0, 34, 68);
    doc.text("Customer Logs Report", 14, 20);

    // Report Info
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(
      `Customer: ${selectedCustomer ? selectedCustomer.name : "All Customers"}`,
      14,
      30,
    );
    doc.text(
      `Period: ${startDate.format("MM/DD/YYYY")} - ${endDate.format("MM/DD/YYYY")}`,
      14,
      36,
    );
    doc.text(`Total Records: ${reportData.length}`, 14, 42);

    let yPosition = 50;

    // Loop through each task log
    for (let idx = 0; idx < reportData.length; idx++) {
      const item = reportData[idx];
      const log = item.taskDetails || {};
      const customer = item.customer || {};
      const taskDate = log.checkOut || log.checkIn || log.updtTs || log.creaTs || log.workOrderTaskDueDate || item.taskDate;

      // Check if we need a new page
      if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = 20;
      }

      // Task Header
      doc.setFillColor(0, 34, 68);
      doc.rect(14, yPosition, pageWidth - 28, 8, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text(
        `Task #${item.taskId || log.taskId || "N/A"} - ${customer.custFirstName || ""} ${customer.custLastName || ""} | ${dayjs(taskDate).format("MM/DD/YYYY")}`,
        16,
        yPosition + 5.5,
      );

      yPosition += 12;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);

      // Create table data for this task
      const tableData = [
        [
          "Log ID",
          log.logId || "N/A",
          "Employee",
          (() => {
            const assignedName = employeeList.find(e => e.id === log.empId)?.name || "";
            return log.taskCompletedBy && assignedName && assignedName !== log.employeeName
              ? `${log.employeeName || "N/A"} (Sub for: ${assignedName})`
              : log.employeeName || "N/A";
          })(),
        ],
        [
          "Check-In",
          log.checkIn ? formatDateTimeWithTz(log.checkIn, customer.custState) : "N/A",
          "Check-Out",
          log.checkOut ? formatDateTimeWithTz(log.checkOut, customer.custState) : "N/A",
        ],
        [
          "Status",
          log.taskStatus || "N/A",
          "Pool Condition",
          log.outCond || "N/A",
        ],
        ["Water Level", log.outWtrLevel || "N/A", "Algae", log.outAlg || "N/A"],
        [
          "pH Level",
          log.phLvl != null ? fmtLevel(log.phLvl) : "N/A",
          "Chlorine Level",
          log.chlrLvl != null ? fmtLevel(log.chlrLvl) : "N/A",
        ],
        [
          "Alkalinity",
          log.alklLvl || "N/A",
          "Stabilizer",
          log.stabLvl || "N/A",
        ],
        [
          "Chlorine Tablets",
          log.chemChlrTab || "N/A",
          "Chlorine Granules",
          log.chemChlrGrn || "N/A",
        ],
        [
          "Chlorine Liquid",
          log.chemChlrLiq || "N/A",
          "Muriatic Acid",
          log.chemMrtcAcd || "N/A",
        ],
        [
          "Soda Ash",
          log.chemSodaAsh || "N/A",
          "Stabilizer Added",
          log.chemStabAdd || "N/A",
        ],
        [
          "Sodium Bicarbonate",
          log.chemSodBic || "N/A",
          "Filter Pressure (PSI)",
          log.filterPressurePsi != null ? String(log.filterPressurePsi) : "N/A",
        ],
        [
          "Clean Pump Basket",
          log.srvcClnPmpBsk || "N/A",
          "Backwash Main",
          log.srvcBckWshMain || "N/A",
        ],
        [
          "Skim Basket",
          log.srvcSkimBsk || "N/A",
          "Vacuum",
          log.srvcVcum || "N/A",
        ],
        [
          "Wall Brush",
          log.srvcWallBrsh || "N/A",
          "Floor Brush",
          log.srvcFlrBrsh || "N/A",
        ],
        [
          "Filter Breakdown",
          log.serviceFilterBreakDown || "N/A",
          "",
          "",
        ],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [],
        body: tableData,
        theme: "grid",
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 35 },
          1: { cellWidth: 55 },
          2: { fontStyle: "bold", cellWidth: 35 },
          3: { cellWidth: 55 },
        },
        margin: { left: 14, right: 14 },
      });

      yPosition = doc.lastAutoTable.finalY + 3;

      // Comments
      if (log.logCmnt || log.srvcCmnt) {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(9);
        doc.setFont(undefined, "bold");
        doc.text("Comments:", 14, yPosition);
        yPosition += 5;

        doc.setFont(undefined, "normal");
        if (log.logCmnt) {
          const logCommentLines = doc.splitTextToSize(
            `Log: ${log.logCmnt}`,
            pageWidth - 28,
          );
          doc.text(logCommentLines, 14, yPosition);
          yPosition += logCommentLines.length * 4 + 2;
        }
        if (log.srvcCmnt) {
          const srvcCommentLines = doc.splitTextToSize(
            `Service: ${log.srvcCmnt}`,
            pageWidth - 28,
          );
          doc.text(srvcCommentLines, 14, yPosition);
          yPosition += srvcCommentLines.length * 4 + 2;
        }
      }

      // Images - try to load and embed actual images
      const images = [];
      if (log.imgEvd) images.push(log.imgEvd);
      if (log.evidences && Array.isArray(log.evidences))
        images.push(...log.evidences);

      if (images.length > 0) {
        if (yPosition > pageHeight - 15) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFont(undefined, "bold");
        doc.setFontSize(9);
        doc.text("Pool Images:", 14, yPosition);
        yPosition += 5;

        for (let i = 0; i < images.length; i++) {
          const fullUrl = getFullImageUrl(images[i]);
          if (fullUrl) {
            try {
              // Try to load and embed the actual image
              const imageData = await loadImageAsBase64(fullUrl);

              // Calculate image dimensions to fit in PDF
              const maxWidth = pageWidth - 28;
              const maxHeight = 80;
              let imgWidth = imageData.width;
              let imgHeight = imageData.height;

              // Scale down if necessary
              const ratio = Math.min(
                maxWidth / imgWidth,
                maxHeight / imgHeight,
                1,
              );
              imgWidth = imgWidth * ratio * 0.264583; // Convert pixels to mm
              imgHeight = imgHeight * ratio * 0.264583;

              // Check if we need a new page
              if (yPosition + imgHeight > pageHeight - 20) {
                doc.addPage();
                yPosition = 20;
              }

              // Add image to PDF using detected format
              doc.addImage(
                imageData.dataURL,
                imageData.format,
                14,
                yPosition,
                imgWidth,
                imgHeight,
              );
              yPosition += imgHeight + 5;
            } catch (error) {
              // If image loading fails, show error message
              console.error(
                `Failed to load image ${i + 1}/${images.length}:`,
                fullUrl,
                error,
              );
              doc.setFont(undefined, "normal");
              doc.setFontSize(8);
              doc.setTextColor(200, 0, 0); // Red color for error
              if (yPosition > pageHeight - 10) {
                doc.addPage();
                yPosition = 20;
              }
              doc.text(
                `[Image failed to load - CORS error or network issue]`,
                14,
                yPosition,
              );
              yPosition += 5;
              doc.setTextColor(0, 0, 0); // Reset to black
              doc.setFontSize(7);
              const urlLines = doc.splitTextToSize(
                `URL: ${fullUrl}`,
                pageWidth - 28,
              );
              doc.text(urlLines, 14, yPosition);
              yPosition += urlLines.length * 3.5 + 3;
              doc.setFontSize(9);
            }
          }
        }
      }

      yPosition += 8; // Space before next task
    }

    // Save the PDF
    const fileName = `Report_${
      selectedCustomer
        ? selectedCustomer.name.replace(/\s/g, "_")
        : "All_Customers"
    }_${startDate.format("YYYY-MM-DD")}_to_${endDate.format("YYYY-MM-DD")}.pdf`;

    try {
      doc.save(fileName);
    } catch (error) {
      console.error("Error saving PDF:", error);
      setErrorMessage("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="reports-container">
        <Paper
          elevation={3}
          sx={{
            padding: "2.5rem",
            margin: "2rem",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
          }}
        >
          {/* Header */}
          <Typography
            variant="h5"
            sx={{
              color: "#002244",
              fontWeight: 600,
              marginBottom: "0.5rem",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            Reports
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#666",
              marginBottom: "2rem",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            Generate and export customer task logs by selecting date range (30
            days, 90 days, 365 days, or custom)
          </Typography>

          {/* Filters Section */}
          <Grid container spacing={3}>
            {/* Customer Search */}
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={customerList}
                getOptionLabel={(option) => `${option.name} - ${option.address}`}
                filterOptions={(options, { inputValue }) => {
                  const q = inputValue.toLowerCase().trim();
                  if (!q) return options;
                  return options.filter((o) =>
                    o.name.toLowerCase().includes(q) ||
                    o.address.toLowerCase().includes(q)
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedCustomer}
                onChange={(event, newValue) => setSelectedCustomer(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Customer (Optional)"
                    placeholder="Select a customer or leave blank for all"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <PersonIcon sx={{ color: "#002244", mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#002244",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#002244",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={5}>
  <Autocomplete
  options={employeeList}
  value={selectedEmployee}
  isOptionEqualToValue={(option, value) => option.id === value.id}
  getOptionLabel={(option) => {
    const duplicates = employeeList.filter(emp => emp.name === option.name);
    if (duplicates.length > 1 && option.phone) {
      return `${option.name} (${option.phone}) - ${option.role}`;
    }
    return `${option.name} - ${option.role}`;
  }}
  onChange={(event, newValue) => setSelectedEmployee(newValue)}
  renderInput={(params) => (
     <TextField
      {...params}
      label="Search Employee (Optional)"
      placeholder="Select an employee or leave blank for all"
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <>
            <PersonIcon sx={{ color: "#002244", mr: 1 }} />
            {params.InputProps.startAdornment}
          </>
        ),
      }}
    />
    )}
    sx={{
      "& .MuiOutlinedInput-root": {
        "&:hover fieldset": {
          borderColor: "#002244",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#002244",
        },
      },
    }}
  />
</Grid>
{/* Generate Report Button */}
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerateReport}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SearchIcon sx={{ fontSize: "1.1rem" }} />
                  )
                }
                sx={{
                  backgroundColor: "#002244",
                  color: "white",
                  padding: "8px 16px",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  fontFamily: "Poppins, sans-serif",
                  textTransform: "none",
                  height: "fit-content",
                  "&:hover": {
                    backgroundColor: "#003366",
                  },
                }}
              >
                {loading ? "Generating..." : "Generate Report"}
              </Button>
            </Grid>


            {/* Date Range Type */}
            <Grid item xs={12} md={6}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#002244",
                    fontWeight: 500,
                    mb: 1,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                </Typography>
                <ToggleButtonGroup
                  value={dateRangeType}
                  exclusive
                  onChange={handleDateRangeChange}
                  aria-label="date range type"
                  fullWidth
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontFamily: "Poppins, sans-serif",
                      "&.Mui-selected": {
                        backgroundColor: "#002244",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#003366",
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="30days">30 Days</ToggleButton>
                  <ToggleButton value="90days">90 Days</ToggleButton>
                  <ToggleButton value="annual">Annual</ToggleButton>
                  <ToggleButton value="custom">Custom</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} md={3}>
              <DatePicker
                label={
                  dateRangeType === "custom"
                    ? "Start Date"
                    : dateRangeType === "30days"
                      ? "Start Date (30 Days)"
                      : dateRangeType === "90days"
                        ? "Start Date (90 Days)"
                        : "Start Date (365 Days)"
                }
                value={startDate}
                onChange={handleStartDateChange}
                disabled={dateRangeType !== "custom"}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: (
                        <CalendarTodayIcon sx={{ color: "#002244", mr: 1 }} />
                      ),
                    },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#002244",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#002244",
                    },
                  },
                }}
              />
            </Grid>

            {/* End Date */}
            <Grid item xs={12} md={3}>
              <DatePicker
                label={
                  dateRangeType === "custom"
                    ? "End Date"
                    : "End Date (Auto-calculated)"
                }
                value={endDate}
                onChange={(newValue) =>{ if (newValue) setEndDate(dayjs(newValue).endOf("day")); }}
                disabled={dateRangeType !== "custom"}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputProps: {
                      startAdornment: (
                        <CalendarTodayIcon sx={{ color: "#002244", mr: 1 }} />
                      ),
                    },
                  },
                }}
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "#002244",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#002244",
                    },
                  },
                }}
              />
            </Grid>

            
          </Grid>

          {/* Error Message */}
          {errorMessage && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#f44336",
                  backgroundColor: "#ffebee",
                  padding: "12px",
                  borderRadius: "4px",
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                {errorMessage}
              </Typography>
            </Box>
          )}

          {/* Results Section */}
          {showResults && (
            <Box sx={{ mt: 4 }}>
              {/* Export Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#002244",
                    fontWeight: 500,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  Report Results
                  <Chip
                    label={`${reportData.length} records`}
                    size="small"
                    sx={{
                      ml: 2,
                      backgroundColor: "#002244",
                      color: "white",
                    }}
                  />
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ViewColumnIcon />}
                    onClick={() => setColPickerOpen(true)}
                    sx={{
                      color: "#002244",
                      borderColor: "#002244",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#003366",
                        backgroundColor: "rgba(0, 34, 68, 0.04)",
                      },
                    }}
                  >
                    Columns {selectedCols.size > 0 && `(${selectedCols.size})`}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={
                      excelLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <TableViewIcon />
                      )
                    }
                    onClick={handleExportToExcel}
                    disabled={reportData.length === 0 || excelLoading}
                    sx={{
                      color: "#002244",
                      borderColor: "#002244",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#003366",
                        backgroundColor: "rgba(0, 34, 68, 0.04)",
                      },
                    }}
                  >
                    {excelLoading ? "Generating Excel..." : "Export to Excel"}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={
                      pdfLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <PictureAsPdfIcon />
                      )
                    }
                    onClick={handleExportToPDF}
                    disabled={reportData.length === 0 || pdfLoading}
                    sx={{
                      color: "#002244",
                      borderColor: "#002244",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#003366",
                        backgroundColor: "rgba(0, 34, 68, 0.04)",
                      },
                    }}
                  >
                    {pdfLoading ? "Generating PDF..." : "Export to PDF"}
                  </Button>
                </Box>
              </Box>

              {/* Column picker dialog */}
              <Dialog open={colPickerOpen} onClose={() => setColPickerOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "Poppins, sans-serif", color: "#002244", fontWeight: 600 }}>
                  Choose Columns
                </DialogTitle>
                <DialogContent dividers>
                  {COL_GROUPS.map((group, gi) => (
                    <Box key={group} sx={{ mb: 1 }}>
                      <Typography sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "0.8rem", color: "#002244", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
                        {group}
                      </Typography>
                      <FormGroup row>
                        {OPTIONAL_COLS.filter(c => c.group === group).map(col => (
                          <FormControlLabel
                            key={col.key}
                            control={
                              <Checkbox
                                checked={selectedCols.has(col.key)}
                                onChange={(e) => {
                                  setSelectedCols(prev => {
                                    const next = new Set(prev);
                                    e.target.checked ? next.add(col.key) : next.delete(col.key);
                                    return next;
                                  });
                                }}
                                sx={{ color: "#002244", "&.Mui-checked": { color: "#002244" } }}
                              />
                            }
                            label={<Typography sx={{ fontFamily: "Poppins, sans-serif", fontSize: "0.875rem" }}>{col.label}</Typography>}
                            sx={{ width: "48%", mr: 0 }}
                          />
                        ))}
                      </FormGroup>
                      {gi < COL_GROUPS.length - 1 && <Divider sx={{ mt: 1 }} />}
                    </Box>
                  ))}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                  <Button onClick={() => setSelectedCols(new Set())} sx={{ textTransform: "none", color: "#666", fontFamily: "Poppins, sans-serif" }}>
                    Clear All
                  </Button>
                  <Button onClick={() => setSelectedCols(new Set(OPTIONAL_COLS.map(c => c.key)))} sx={{ textTransform: "none", color: "#666", fontFamily: "Poppins, sans-serif" }}>
                    Select All
                  </Button>
                  <Button variant="contained" onClick={() => setColPickerOpen(false)} sx={{ textTransform: "none", backgroundColor: "#002244", fontFamily: "Poppins, sans-serif", "&:hover": { backgroundColor: "#003366" } }}>
                    Done
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Results Table */}
              {reportData.length > 0 ? (
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: 500,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={HEADER_CELL_SX}>Task ID</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Customer</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Employee</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Date</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Check-In</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Check-Out</TableCell>
                        {OPTIONAL_COLS.filter(c => selectedCols.has(c.key)).map(col => (
                          <TableCell key={col.key} sx={HEADER_CELL_SX}>{col.label}</TableCell>
                        ))}
                        <TableCell sx={HEADER_CELL_SX}>Status</TableCell>
                        <TableCell sx={HEADER_CELL_SX}>Images</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportData.map((row, index) => {
                        const log = row.taskDetails || {};
                        const customer = row.customer || {};
                        const taskDate = log.checkOut || log.checkIn || log.updtTs || log.creaTs || log.workOrderTaskDueDate || row.taskDate;
                        const hasImages =
                          Array.isArray(log.evidences) && log.evidences.some(e => getFullImageUrl(e) !== null);

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                              "&:hover": { backgroundColor: "#f0f0f0" },
                            }}
                          >
                            <TableCell sx={{ fontFamily: "Poppins, sans-serif" }}>
                              {row.taskId || log.taskId || "N/A"}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Poppins, sans-serif" }}>
                              {log.customerName || "N/A"}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Poppins, sans-serif" }}>
                              {(() => {
                                if (log.taskCompletedBy) {
                                  const assigned = employeeList.find(e => e.id === log.empId);
                                  return (
                                    <span>
                                      {log.employeeName || "N/A"}
                                      {assigned && assigned.name !== log.employeeName && (
                                        <span style={{ color: "#888", fontSize: "0.78rem", display: "block" }}>
                                          Sub for: {assigned.name}
                                        </span>
                                      )}
                                    </span>
                                  );
                                }
                                return log.employeeName || "N/A";
                              })()}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Poppins, sans-serif" }}>
                              {taskDate ? dayjs(taskDate).format("MM/DD/YYYY") : "N/A"}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Poppins, sans-serif", fontSize: "0.85rem" }}>
                              {log.checkIn ? formatTimeWithTz(log.checkIn, customer.custState) : "N/A"}
                            </TableCell>
                            <TableCell sx={{ fontFamily: "Poppins, sans-serif", fontSize: "0.85rem" }}>
                              {log.checkOut ? formatTimeWithTz(log.checkOut, customer.custState) : "N/A"}
                            </TableCell>
                            {OPTIONAL_COLS.filter(c => selectedCols.has(c.key)).map(col => (
                              <TableCell key={col.key} sx={{ fontFamily: "Poppins, sans-serif", fontSize: "0.85rem" }}>
                                {col.render(log)}
                              </TableCell>
                            ))}
                            <TableCell>
                              <Chip
                                label={log.taskStatus || "N/A"}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    log.taskStatus === "Completed" ? "#4caf50" :
                                    log.taskStatus === "Cancelled" ? "#f44336" :
                                    log.taskStatus === "Pending"   ? "#ff9800" : "#757575",
                                  color: "white",
                                  fontFamily: "Poppins, sans-serif",
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {hasImages ? (
                                <Chip
                                  label="View"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#002244",
                                    color: "white",
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#003366" },
                                  }}
                                  onClick={() => {
                                    const images = (log.evidences || []).filter(e => e && e.trim());
                                    handleViewImages(images);
                                  }}
                                />
                              ) : (
                                <Typography sx={{ fontSize: "0.85rem", color: "#999" }}>
                                  None
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper
                  sx={{
                    padding: "3rem",
                    textAlign: "center",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#666",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    No task logs found for the selected criteria
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </Paper>
      </Box>
      {/* Evidence image viewer modal */}
      <Dialog open={imageViewerOpen} onClose={handleCloseImageViewer} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
          Evidence Photos
        </DialogTitle>
        <DialogContent>
          {viewerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : viewerImages.length === 0 ? (
            <Typography sx={{ fontFamily: "Poppins, sans-serif", color: "#666", textAlign: "center", py: 4 }}>
              No images could be loaded.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center", py: 1 }}>
              {viewerImages.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`Evidence ${i + 1}`}
                  style={{ maxWidth: "300px", maxHeight: "300px", objectFit: "contain", borderRadius: 8, border: "2px solid #ddd" }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageViewer} sx={{ fontFamily: "Poppins, sans-serif" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default Reports;
